import { useMemo } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  IconCloudDownload,
  IconEye,
  IconFiles,
  IconFileText
} from "@tabler/icons-react";
import MxDetailSmallCard from "@rf-l1/page/details/MxDetailCardSmall";
import { usePropertyDetailPage } from "../../context/PropertyDetailPageContext";
import useUserHasPermission from "@/shared/hooks/use-useris.hook";
import { useLoggedUser } from "@/shared/store/auth";
import useShowFileModal from "@rf-hooks/useShowFileModal";
import download from "downloadjs";
import axios from "axios";
import toast from "react-hot-toast";
import { capitalizeName } from "@/src-refactor/modules/shared/components/typography/text-simple/TextSimple";
import { Typography } from "@mui/material";
import useDownloadDocument from "@/src-refactor/modules/shared/hooks/useDowloadFile";
import MxTooltipNormal from "@rf-l1/tooltip/MxTooltipNormal";

/**
 * PropertyDocumentsCard
 *
 * Tarjeta que muestra los documentos asociados a la propiedad.
 * Permite descargar, visualizar y abrir en nueva pestaña.
 *
 * Reglas de visibilidad:
 * - Visible para Super Admin
 * - Visible para Agentes captadores de la propiedad
 * - Visible para usuario específico (gracielamarte@remaxm.net) - Legacy
 */
const PropertyDocumentsCard = () => {
  const { documents = [], agents = [], id } = usePropertyDetailPage();
  const { USER_ROLE } = useUserHasPermission();
  const { userLogged } = useLoggedUser();
  const { downloadDocument } = useDownloadDocument();
  const { showFileModal } = useShowFileModal();

  const canViewDocuments = useMemo(() => {
    // Legacy rule from original component
    if (userLogged.userData?.email === "gracielamarte@remaxm.net") {
      return true;
    }

    if (USER_ROLE.SUPER_ADMIN) {
      return true;
    }

    const isAgent = agents.some(
      agent => agent.id === userLogged.userData?.people_id
    );
    if (isAgent) {
      return true;
    }

    return false;
  }, [agents, userLogged, USER_ROLE]);

  if (!canViewDocuments || documents.length === 0) {
    return null;
  }

  const handleDownload = async (url: string, name: string) => {
    downloadDocument(url, name);
  };

  const handleOpenInNewWindow = (url: string) => {
    if (!url) return;
    window.open(url, "_blank");
  };

  const handleView = (url: string) => {
    showFileModal({
      url: url
    });
  };

  return (
    <MxDetailSmallCard title="Documentos">
      <ContainerButtons>
        {documents.map(document => (
          <DocumentRow key={document.id}>
            <ActionsContainer>
              <MxTooltipNormal label="Descargar documento" placement="bottom">
                <ActionButton
                  size="small"
                  onClick={() => handleDownload(document.url, document.name)}
                >
                  <IconCloudDownload size={20} />
                </ActionButton>
              </MxTooltipNormal>

              <MxTooltipNormal label="Ver documento" placement="bottom">
                <ActionButton size="small" onClick={() => handleView(document.url)}>
                  <IconEye size={20} />
                </ActionButton>
              </MxTooltipNormal>

              <MxTooltipNormal
                label="Abrir documento en otra pestaña"
                placement="bottom"
              >
                <ActionButton
                  size="small"
                  onClick={() => handleOpenInNewWindow(document.url)}
                >
                  <IconFiles size={20} />
                </ActionButton>
              </MxTooltipNormal>
            </ActionsContainer>

            <DocumentName variant="body2">
              {capitalizeName(document.name, "first")}
            </DocumentName>
          </DocumentRow>
        ))}
      </ContainerButtons>
    </MxDetailSmallCard>
  );
};

export default PropertyDocumentsCard;

const ContainerButtons = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  width: "100%"
}));

const DocumentRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  width: "100%"
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5)
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: theme.spacing(0.5),
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));

const DocumentName = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 400,
  fontFamily: "Inter, sans-serif" // Keeping Inter as per original, though theme font is preferred usually
}));
